import React, { useRef, useEffect, useState, memo } from 'react';
import { Tag } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';

// 重点信号配置
const KEY_SIGNALS = [
  { pattern: 'knee_angle', label: '膝角', color: '#1890ff', unit: '°' },
  { pattern: 'elbow_angle', label: '肘角', color: '#52c41a', unit: '°' },
  { pattern: 'hip_angle', label: '髋角', color: '#722ed1', unit: '°' },
  { pattern: 'trunk_tilt', label: '躯干倾角', color: '#fa8c16', unit: '°' },
  { pattern: 'shoulder_abduction', label: '肩外展角', color: '#eb2f96', unit: '°' },
  { pattern: 'body_center_sway', label: '重心摆动', color: '#13c2c2', unit: 'norm' },
  { pattern: 'knee_center_y', label: '膝中心高度', color: '#faad14', unit: 'norm' },
  { pattern: 'hip_center_y', label: '髋中心高度', color: '#f5222d', unit: 'norm' },
];

// 绘制单个信号波形
const drawWaveform = (canvas, signal) => {
  if (!canvas || !signal) return;
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;

  const values = signal.values || [];
  const fps = signal.fps || 15;
  if (values.length === 0) return;

  const padding = { top: 20, right: 10, bottom: 25, left: 40 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  // 清空
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, 0, width, height);

  // 计算值范围
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  const yMin = minVal - range * 0.1;
  const yMax = maxVal + range * 0.1;
  const yRange = yMax - yMin;

  // 绘制网格
  ctx.strokeStyle = '#2a2a2a';
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= 4; i++) {
    const y = padding.top + (chartH * i) / 4;
    ctx.beginPath();
    ctx.moveTo(padding.left, y);
    ctx.lineTo(padding.left + chartW, y);
    ctx.stroke();
    // Y轴标签
    ctx.fillStyle = '#666';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    const val = yMax - (yRange * i) / 4;
    ctx.fillText(val.toFixed(1), padding.left - 4, y + 3);
  }

  // 绘制波形
  ctx.strokeStyle = signal.color || '#1890ff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  values.forEach((val, i) => {
    const x = padding.left + (i / (values.length - 1)) * chartW;
    const y = padding.top + ((yMax - val) / yRange) * chartH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // 标题
  ctx.fillStyle = signal.color || '#1890ff';
  ctx.font = 'bold 11px sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(signal.label || signal.name, padding.left + 4, 14);

  // 单位
  if (signal.unit) {
    ctx.fillStyle = '#666';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(signal.unit, width - padding.right, 14);
  }

  // X轴时间标签
  ctx.fillStyle = '#666';
  ctx.font = '9px monospace';
  ctx.textAlign = 'center';
  const duration = values.length / fps;
  for (let i = 0; i <= 4; i++) {
    const t = (duration * i) / 4;
    const x = padding.left + (chartW * i) / 4;
    ctx.fillText(t.toFixed(1) + 's', x, height - 6);
  }
};

const SignalWaveform = memo(({ layer2Url }) => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const signalDataRef = useRef({});

  useEffect(() => {
    if (!layer2Url) {
      console.log('SignalWaveform: No layer2Url provided');
      return;
    }
    console.log('SignalWaveform: Fetching from', layer2Url);
    setLoading(true);
    fetch(layer2Url)
      .then(res => {
        console.log('SignalWaveform: Response status', res.status);
        return res.json();
      })
      .then(data => {
        console.log('SignalWaveform: Data structure:', {
          schemaVersion: data.schemaVersion,
          signalCount: data.signalCount,
          signalKeys: Object.keys(data.signals || {}),
        });

        // 解析 Layer 2 数据 - 展示所有信号
        const signalMap = data.signals || data;
        const parsed = [];
        const processedKeys = new Set();

        // 先处理预定义的关键信号（优先显示，用预定义颜色）
        KEY_SIGNALS.forEach(config => {
          const key = Object.keys(signalMap).find(k =>
            k.toLowerCase().includes(config.pattern.toLowerCase())
          );
          if (key && signalMap[key]) {
            const sig = signalMap[key];
            const values = sig.smoothed_values || sig.raw_values || (sig.values || []).map(v => v.value).filter(v => v != null);
            if (values.length > 0) {
              const signalData = {
                name: key,
                label: sig.description || config.label,
                color: config.color,
                unit: config.unit || sig.unit || '',
                values: values,
                fps: sig.fps || data.fps || 15,
              };
              parsed.push(signalData);
              signalDataRef.current[parsed.length - 1] = signalData;
              processedKeys.add(key);
            }
          }
        });

        // 再添加剩余的所有信号（用随机颜色）
        const colors = ['#1890ff', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2', '#faad14', '#f5222d', '#2f54eb', '#a0d911'];
        let colorIdx = parsed.length % colors.length;

        Object.entries(signalMap).forEach(([name, sig]) => {
          if (processedKeys.has(name)) return; // 跳过已处理的

          const values = sig.smoothed_values || sig.raw_values || (sig.values || []).map(v => v.value).filter(v => v != null);
          if (values.length > 0) {
            const signalData = {
              name,
              label: sig.description || sig.signal_name || name,
              color: colors[colorIdx % colors.length],
              unit: sig.unit || '',
              values: values,
              fps: sig.fps || data.fps || 15,
            };
            parsed.push(signalData);
            signalDataRef.current[parsed.length - 1] = signalData;
            colorIdx++;
          }
        });

        console.log('SignalWaveform: Parsed signals:', parsed.length);
        setSignals(parsed);
        setLoading(false);
      })
      .catch(err => {
        console.error('SignalWaveform: Fetch error', err);
        setLoading(false);
      });
  }, [layer2Url]);

  // Canvas ref callback - 在 canvas 元素创建时立即绘制
  const setCanvasRef = (canvas, idx) => {
    if (canvas) {
      const signalData = signalDataRef.current[idx];
      if (signalData) {
        drawWaveform(canvas, signalData);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ background: '#1a1a1a', borderRadius: 8, padding: 20, textAlign: 'center' }}>
        <span style={{ color: '#666', fontSize: 12 }}>加载信号数据...</span>
      </div>
    );
  }

  if (signals.length === 0) return null;

  console.log('Rendering', signals.length, 'signal waveforms');

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 16px', marginBottom: 16, border: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <LineChartOutlined style={{ color: '#1890ff', marginRight: 6 }} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>信号波形</span>
        <Tag color="blue" style={{ marginLeft: 8 }}>{signals.length} 个信号</Tag>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        {signals.map((signal, idx) => (
          <div key={idx} style={{ background: '#1a1a1a', borderRadius: 6, overflow: 'hidden', border: '1px solid #333' }}>
            <canvas
              ref={el => setCanvasRef(el, idx)}
              width={320}
              height={140}
              style={{ width: '100%', height: 140, display: 'block' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default SignalWaveform;
